import { sendAnalytics } from '@playcastdotio/Analytics';
import type { QuickstartState } from '../../QuickstartCoordinator';
import type { BaseAnalyticsProps } from '../IQuickstartAnalyticsManager';

export function sendAFKTimerStartedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  timeFromJoinMs: number,
): void {
  sendAnalytics({
    name: 'AFK Timer Started',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? '',
    broadcastId: state.broadcastId ?? '',
    timeoutSeconds: state.afkTimeoutSeconds,
    timeFromJoinMs,
  });
}

export function sendAFKTimeoutReachedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  sessionDuration: number,
): void {
  sendAnalytics({
    name: 'AFK Timeout Reached',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? '',
    broadcastId: state.broadcastId ?? '',
    afkTimeoutSeconds: state.afkTimeoutSeconds,
    sessionDuration,
  });
}

export function sendFirstInputDetectedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  timeToFirstInput: number,
  inputDeviceType?: 'keyboard' | 'mouse' | 'touch' | 'gamepad',
): void {
  sendAnalytics({
    name: 'First Input Detected',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? '',
    broadcastId: state.broadcastId ?? '',
    timeToFirstInput,
    inputDeviceType,
  });
}

export function sendAdTimerStartedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
): void {
  sendAnalytics({
    name: 'Ad Timer Started',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? '',
    broadcastId: state.broadcastId ?? '',
    durationSeconds: state.adTimerSeconds,
  });
}

export function sendAdTimerExpiredEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  totalPlaytime: number,
): void {
  sendAnalytics({
    name: 'Ad Timer Expired',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? '',
    broadcastId: state.broadcastId ?? '',
    totalPlaytime,
  });
}

export function sendWishlistClickedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  playtime: number,
  uiVariant?: string,
  buttonPosition?: string,
  elapsedSinceTimerStartSeconds?: number,
): void {
  sendAnalytics({
    name: 'Wishlist Clicked',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? '',
    broadcastId: state.broadcastId ?? '',
    playtime,
    uiVariant,
    buttonPosition,
    elapsedSinceTimerStartSeconds,
  });
}

export function sendCTAPageViewedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  totalSessionDuration: number,
  ctaType?: string,
  uiVariant?: string,
  referrerPage?: string,
): void {
  sendAnalytics({
    name: 'CTA Page Viewed',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? undefined,
    broadcastId: state.broadcastId ?? undefined,
    exitReason: state.exitReason ?? 'manual_exit',
    totalSessionDuration,
    ctaType,
    uiVariant,
    referrerPage,
  });
}

type WishlistTriggerSource = 'cta_page' | 'thank_you' | 'matchmaking_failed' | 'broadcast_join_failed' | 'error' | 'auth_failed' | 'auto_redirect' | 'afk_timer_hit' | 'maxed_out_game_time' | 'loading_timeout';

export function sendWishlistCTAClickedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  steamUrl: string,
  triggerSource?: WishlistTriggerSource,
  providerInferred?: string,
  providerSupported?: boolean,
  providerSelected?: string,
  dedupRuleApplied?: boolean,
): void {
  sendAnalytics({
    name: 'Wishlist CTA Clicked',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? undefined,
    broadcastId: state.broadcastId ?? undefined,
    steamUrl,
    triggerSource,
    providerInferred,
    providerSupported,
    providerSelected,
    dedupRuleApplied,
  });
}

export function sendAuthAttemptedEvent(baseProps: BaseAnalyticsProps, state: QuickstartState): void {
  sendAnalytics({
    name: 'Auth Attempted',
    role: 'player',
    ...baseProps,
    invitationId: state.matchId ?? undefined,
    broadcastId: state.broadcastId ?? undefined,
  });
}

export function sendAuthFailedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  authProvider: string,
  reason: string,
  attemptId?: string,
  failureReason?: 'denied' | 'closed' | 'network' | 'provider_error' | 'timeout' | 'unknown',
  errorCode?: string,
  errorMessage?: string,
): void {
  sendAnalytics({
    name: 'Auth Failed',
    role: 'player',
    ...baseProps,
    invitationId: state.matchId ?? undefined,
    broadcastId: state.broadcastId ?? undefined,
    authProvider,
    reason,
    attemptId,
    failureReason,
    errorCode,
    errorMessage,
  });
}

export function sendAuthSucceededEvent(
  baseProps: BaseAnalyticsProps,
  authProvider: string,
  playerAuthId: string,
  attemptId?: string,
  accountState?: 'signup' | 'login',
): void {
  sendAnalytics({
    name: 'User Authenticated',
    role: 'player',
    ...baseProps,
    playerAuthId,
    source: 'browser',
    authProvider,
    attemptId,
    accountState,
  });
}

export function sendThankYouPageViewedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string,
  uiVariant?: string,
): void {
  sendAnalytics({
    name: 'Thank You Page Viewed',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? undefined,
    broadcastId: state.broadcastId ?? undefined,
    uiVariant,
  });
}

export function sendWishlistEmailSentEvent(
  baseProps: BaseAnalyticsProps,
  playerAuthId: string,
  email: string,
): void {
  sendAnalytics({
    name: 'Wishlist Emailed',
    role: 'player',
    ...baseProps,
    playerAuthId,
    hostAuthId: playerAuthId,
    email,
  });
}

export function sendDeveloperLogoClickedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  defaultPlayerAuthId: string | undefined,
  destinationId: string,
  openBehavior: 'new_tab' | 'same_tab',
  playerAuthId?: string,
): void {
  sendAnalytics({
    name: 'Developer Logo Clicked',
    role: 'player',
    ...baseProps,
    playerAuthId: playerAuthId ?? defaultPlayerAuthId,
    invitationId: state.matchId ?? undefined,
    broadcastId: state.broadcastId ?? undefined,
    destinationId,
    openBehavior,
  });
}

export function sendThankYouRedirectEvent(
  baseProps: BaseAnalyticsProps,
  steamUrl: string,
  userAuthId?: string,
): void {
  sendAnalytics({
    name: 'Thank You Redirect',
    role: 'player',
    ...baseProps,
    steamUrl,
    userAuthId,
  });
}

export function sendPlayerExitedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  reason: 'close' | 'navigation' | 'timeout' | 'error' | 'unknown',
  sessionDuration?: number,
  totalSessionDuration?: number,
): void {
  sendAnalytics({
    name: 'Player Exited',
    role: 'player',
    ...baseProps,
    persistentId: baseProps.persistentId || 'unknown',
    playerAuthId,
    playerConnectionId: state.playerConnectionId,
    broadcastSessionId: state.broadcastId,
    invitationId: state.matchId,
    reason,
    sessionDuration,
    totalSessionDuration,
  });
}

export function sendTabVisibilityChangedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  visibilityState: 'unfocus' | 'focus',
  sessionDuration?: number,
  totalSessionDuration?: number,
): void {
  sendAnalytics({
    name: 'Tab Visibility Changed',
    role: 'player',
    ...baseProps,
    playerAuthId,
    playerConnectionId: state.playerConnectionId,
    broadcastSessionId: state.broadcastId,
    invitationId: state.matchId,
    visibilityState,
    sessionDuration,
    totalSessionDuration,
  });
}
